// @flow
import React, { Component, Fragment } from 'react';
import classNames from 'classnames/bind';
import PropTypes from 'prop-types';
import { animateScroll as scroll } from 'react-scroll';
import { FormattedMessage } from 'react-intl';
import Checkbox from '../../../../../elements/Checkbox/Checkbox';
import { debounceTime } from 'rxjs/operators';
import Loading from '../../../../../elements/Loading/Loading';
import Modal from '../../../../../elements/Modal/Modal';
import { Subject, Subscription } from 'rxjs';
import Select from '../../../../../elements/Select/Select';
import ReportModalContainer from '../../../../report/container/ReportModalContainer';
import FloatingBottom from '../../../../../elements/FloatingBottom/FloatingBottom';
import update from 'immutability-helper';
import { FormValidator } from '../../../../../utils/form';

import style from './Purchased.scss';
import commonStyle from '../../../components/OrderCommon.scss';
import icons from '../../../../../assets/styles/icons.scss';
import inputStyle from '../../../../../assets/styles/input.scss';
import buttonStyle from '../../../../../assets/styles/button.scss';
import formStyle from '../../../../../assets/styles/form.scss';

import type { Node } from 'react';
import type { Order, OrderItem } from '../../../../../entities';
import type { Props as ProductProps } from '../../../../../elements/Product/Product';
import type { CancellationMotive, TrackingInfo } from '../duck';
import type { LocaleState } from '../../../../locale/duck';
import type { Option } from '../../../../../elements/Select/Select';
import type { HTTPError } from '../../../../../error';
import type { Motive, MotiveKey } from '../../../../report/duck';

type Props = {
  +order: Order;
  +buildProducts: (renderContent?: (props: ProductProps) => Node) => Node;
  +buildSummary: () => Node;
  // +buildPurchaserInfo: () => Node;
  +setTrackingNumber: (
    orderId: string,
    itemNumber: number,
    trackingInfo: TrackingInfo,
  ) => void;
  +confirmItemReceived: (orderId: string, itemNumber: number, isChecked: boolean) => void;
  +progressReceive: (orderId: string) => void;
  +itemReceivedLoadingMap: { [key: string]: boolean };
  +itemTrackingNumberLoadingMap: {
    [key: string]: boolean;
  };
  +itemTrackingNumberErrorMap: {
    [key: string]: HTTPError;
  };
  +itemLoadingCancelMap: { [key: string]: boolean };
  +itemCancelErrorMap: { [key: string]: HTTPError };
  +isProgressing?: boolean;
  +fetchCancellationMotives: () => void;
  +cancelItem: (orderId: string, itemNumber: number, motive: string, message: ?string) => void;
  +motives?: CancellationMotive[];
  +reportMotives?: { [key: MotiveKey]: Motive[] };
  +locale: LocaleState;
};

type State = {
  isOpenCancelModal: boolean;
  cancelModalItemNumber?: number;
  cancelationMotive: string;
  cancelationOtherText: string;
  itemValues: { [key: string]: { [key: string]: string } };
  reportModalVisible: boolean;
};

export default class Purchased extends Component<Props, State> {
  changeTrackingSubject$: Subject = new Subject();
  changeTrackingSubscription: Subscription;

  constructor(props: Props) {
    super(props);
    this.state = {
      isOpenCancelModal: false,
      cancelationMotive: '',
      cancelationOtherText: '',
      itemValues: {},
      reportModalVisible: false,
    };
  }

  componentDidMount() {
    const { fetchCancellationMotives, order } = this.props;
    this.changeTrackingSubscription = this.changeTrackingSubject$.pipe(debounceTime(600)).subscribe({
      next: ({ itemNumber, trackingNumber, trackingUrl, trackingCourier }) => {
        const { setTrackingNumber, order } = this.props;
        setTrackingNumber(order.id, itemNumber, { trackingCourier, trackingNumber, trackingUrl });
      },
    });
    fetchCancellationMotives();
    if (order) {
      this.setTrackingState(order);
    }
  }

  componentWillUnmount() {
    if (this.changeTrackingSubject$) {
      this.changeTrackingSubject$.unsubscribe();
    }
    if (this.changeTrackingSubscription) {
      this.changeTrackingSubscription.unsubscribe();
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { itemLoadingCancelMap, order, itemCancelErrorMap, progressReceive } = this.props;
    const { cancelModalItemNumber } = this.state;
    if (typeof cancelModalItemNumber === 'number') {
      if (!!prevProps.itemLoadingCancelMap[`${cancelModalItemNumber}`] &&
        !itemLoadingCancelMap[`${cancelModalItemNumber}`]) {
        this.handleCloseCancelModal();
      }
      if (!prevProps.itemCancelErrorMap[`${cancelModalItemNumber}`] &&
        !!itemCancelErrorMap[`${cancelModalItemNumber}`]) {
        this.scrollAnimateTop();
      }
    }
    if (!prevProps.order && !!order) {
      this.setTrackingState(order);
    }

    if (!!order && order !== prevProps.order) {
      if (order.items.length - this.numItemsCancelled() === 0) {
        progressReceive(order.id);
      }
    }
  }

  setTrackingState(order: Order) {
    order.items.forEach((item: OrderItem) => {
      this.setState((state: State) => ({
        ...state,
        itemValues: {
          ...state.itemValues,
          [`${item.number}`]: {
            trackingNumber: item.tracking ? item.tracking.trackingNumber : "",
            trackingUrl: item.tracking ? item.tracking.trackingUrl : "",
          },
        },
      }));
    });
  }

  handleCheckoutChange = (itemNumber: number, index: number) => {
    const { confirmItemReceived, order } = this.props;
    confirmItemReceived(order.id, itemNumber, !(order.items[index].state === 'CONFIRMED'));
  };

  handleChange = (e: SyntheticInputEvent<HTMLInputElement>, item: OrderItem) => {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      this.setState((st: State) => update(st, {
        itemValues: {
          [`${item.number}`]: {
            $set: {
              ...st.itemValues[`${item.number}`],
              [name]: value,
            },
          },
        },
      }));
      const { trackingNumber, trackingUrl } = this.state.itemValues[`${item.number}`];
      if (name === 'trackingNumber') {
        if (trackingUrl) {
          this.changeTrackingSubject$.next({
            itemNumber: item.number,
            trackingNumber: value,
            trackingUrl,
            trackingCourier: 'AMZL',
          });
        } else if (!value.startsWith('TBA')) {
          this.changeTrackingSubject$.next({
            itemNumber: item.number,
            trackingNumber: value,
            trackingCourier: 'OTHER',
          });
        }
      } else if (name === 'trackingUrl') {
        this.changeTrackingSubject$.next({
          itemNumber: item.number,
          trackingNumber,
          trackingUrl: value,
          trackingCourier: 'AMZL',
        });
      }
    }
  };

  scrollAnimateTop = () =>
    scroll.scrollTo(0, {
      duration: 400,
    });

  handleOpenCancelModal = (itemNumber: number) => {
    this.setState({
      isOpenCancelModal: true,
      cancelModalItemNumber: itemNumber,
    });
  };

  handleCloseCancelModal = () => {
    this.setState({
      isOpenCancelModal: false,
      cancelModalItemNumber: undefined,
      cancelationMotive: '',
      cancelationOtherText: '',
    });
  };

  renderContent = (props: ProductProps): Node => {
    const { itemValues } = this.state;
    const {
      itemReceivedLoadingMap,
      itemTrackingNumberLoadingMap,
      itemTrackingNumberErrorMap,
      order,
    } = this.props;
    const { index, item } = props;
    const trackingUrlError: ?HTTPError = itemTrackingNumberErrorMap[`${item.number}`]
    let trackingUrlLabel = undefined;
    if (
      trackingUrlError &&
      trackingUrlError.code === 400 &&
      trackingUrlError.data &&
      trackingUrlError.data['message'] === 'Invalid URL'
    ) {
      trackingUrlLabel = FormValidator.buildErrorLabel(
        ['InvalidTrackingNumber'],
        {
          InvalidTrackingNumber: 'order.purchase.courierFlow.products.trackingUrl.invalidTrackingNumber',
        },
      );
    }
    return (
      <section className={style.receivedWrap}>
        <section className={style.checkoutControl}>
          <FormattedMessage id="order.sale.purchased.item.received.title">{
            text => <p>{text}</p>
          }</FormattedMessage>
          <section className={style.checkboxWrap}>
            <article className={classNames(style.leftIconWrap,
              { [style.checked]: order.items[index].state === 'CONFIRMED' })}>
              {/*<i className={icons.iconPictureMedium}/>*/}
              {/*<pre>{index}: {JSON.stringify(order.items[index].state)}</pre>*/}
              <i className={classNames(icons.iconDeliverBoxMedium)}/>
            </article>
            <article className={style.middleWrap}>
              <FormattedMessage id="order.sale.purchased.item.received.label">{
                txt => <Checkbox isChecked={order.items[index].state === 'CONFIRMED'}
                                 handleChange={() => this.handleCheckoutChange(item.number, index)}
                                 disabled={itemReceivedLoadingMap[`${item.number}`]}
                                 name='isReceived'
                                 color='orange'
                                 className={classNames(style.checkbox,
                                   { [style.disabled]: itemReceivedLoadingMap[`${item.number}`] })}
                                 value='isReceived'>{txt}</Checkbox>
              }</FormattedMessage>
            </article>
          </section>
          {/*<section className={style.checkoutBox}>
            <i className={classNames(icons.iconDeliverBoxMedium,
              { [style.checked]: order.items[props.index].state === 'CONFIRMED' })}/>
            <FormattedMessage id="order.sale.purchased.item.received.label">{
              txt => <Checkbox isChecked={order.items[props.index].state === 'CONFIRMED'}
                               handleChange={() => this.handleCheckoutChange(props.index)}
                               disabled={itemReceivedLoadingMap[`${index}`]}
                               name='isReceived'
                               color='orange'
                               className={classNames({ [style.disabled]: itemReceivedLoadingMap[`${props.index + 1}`] },
                                 style.checkout)}
                               value='isReceived'>{txt}</Checkbox>
            }</FormattedMessage>
          </section>*/}
        </section>
        <section className={style.inputControl}>
          <FormattedMessage id="order.sale.purchased.item.trackingNumber.title">{
            text => <p>{text}</p>
          }</FormattedMessage>
          <input type="text"
                 name="trackingNumber"
                 className={classNames(inputStyle.input)}
                 value={itemValues[`${item.number}`] ? itemValues[`${item.number}`].trackingNumber : ""}
                 onChange={(e: SyntheticInputEvent<HTMLInputElement>) => this.handleChange(e, item)}/>
          {itemTrackingNumberLoadingMap[`${item.number}`] && <Loading type="sk-three-bounce"
                                                                      color='orange'
                                                                      number={3}
                                                                      className={style.loading}/>}
        </section>
        {item.onlineStore.id === "ST04287850" &&
        <section className={style.inputControl}>
          <FormattedMessage id="order.sale.purchased.item.trackingUrl.title">{
            text => <p>{text}</p>
          }</FormattedMessage>
          <FormattedMessage id="order.sale.purchased.item.trackingUrl.placeholder">{
            txt => <input type="text"
                          name="trackingUrl"
                          className={classNames(
                            inputStyle.input,
                            { [inputStyle.red]: !!trackingUrlLabel },
                          )}
                          placeholder={txt}
                          value={itemValues[`${item.number}`] ? itemValues[`${item.number}`].trackingUrl : ""}
                          disabled={itemValues[`${item.number}`] && !itemValues[`${item.number}`].trackingNumber}
                          onChange={(e: SyntheticInputEvent<HTMLInputElement>) => this.handleChange(e, item)}/>
          }</FormattedMessage>
          {itemTrackingNumberLoadingMap[`${item.number}`] && <Loading type="sk-three-bounce"
                                                                      color='orange'
                                                                      number={3}
                                                                      className={style.loading}/>}
          {trackingUrlLabel}
        </section>}
        <FormattedMessage id="order.sale.purchased.item.cancel.button">{
          text => <p className={style.cancel}
                     onClick={_ => this.handleOpenCancelModal(item.number)}>{text}</p>
        }</FormattedMessage>
      </section>);
  };

  isButtonDisabled = (): boolean => {
    const { order, itemReceivedLoadingMap } = this.props;
    for (let i = 0; i < order.items.length; i++) {
      const item = order.items[i];
      if (item.state === 'PENDING' || !!itemReceivedLoadingMap[`${item.number}`]) {
        return true;
      }
    }
    return false;
  };

  handleChangeCancelOther(e: Event) {
    if (e.target instanceof HTMLInputElement ||
      e.target instanceof HTMLTextAreaElement) {
      const { name, value } = e.target;
      this.setState({ cancelationOtherText: value });
    }
  }

  handleCancelItem = () => {
    const { order, cancelItem } = this.props;
    const { cancelModalItemNumber, cancelationMotive, cancelationOtherText } = this.state;
    if (typeof cancelModalItemNumber === 'number') {
      cancelItem(order.id, cancelModalItemNumber, cancelationMotive, cancelationOtherText);
    }
  };

  handleCloseReportModal = () => {
    this.setState({ reportModalVisible: false });
  };

  handleOpenReportModal = () => {
    this.setState({ reportModalVisible: true });
  };

  numItemsCancelled = (): number => {
    const { order } = this.props;
    return order.items.reduce((total: number, item: OrderItem) => {
      if (item.cancelledCount === item.amount) {
        total++;
      }
      return total;
    }, 0);
  };

  render() {
    const {
      buildProducts, buildSummary, progressReceive,
      order, motives, locale, itemLoadingCancelMap, cancelItem, reportMotives,
    } = this.props;
    const {
      isOpenCancelModal, cancelationMotive, cancelationOtherText, cancelModalItemNumber, reportModalVisible,
    } = this.state;
    const modalOptions = motives && motives.map((m: CancellationMotive) => ({
      value: m.name,
      label: m.translations[locale.locale.toUpperCase()],
    }));
    const options = reportMotives ? reportMotives.traveler.map((motive: Motive) => ({
      value: motive.name,
      label: motive.translations[locale.locale.toUpperCase()],
    })) : [];

    return (<Fragment>
        <main className={commonStyle.mainWrap}>
          <section className={classNames(commonStyle.wrap, commonStyle.leftWrap)}>
            {buildProducts(this.renderContent)}
          </section>
          <section className={classNames(commonStyle.wrap, commonStyle.rightWrap)}>
            {buildSummary()}
            <FormattedMessage id="order.sale.purchased.continue.button">{
              txt => <button className={classNames(buttonStyle.btn, buttonStyle.orange, style.continue)}
                             disabled={this.isButtonDisabled()}
                             onClick={_ => progressReceive(order.id)}>{txt}</button>
            }</FormattedMessage>
            <FormattedMessage id="order.sale.purchased.report.button">{
              txt => <button className={classNames(buttonStyle.btn, buttonStyle.redInverse, style.report)}
                             onClick={this.handleOpenReportModal}>{txt}</button>
            }</FormattedMessage>
          </section>
          <section className={style.buttonsWrap}>
            <FloatingBottom offSetY={152}>
              <FormattedMessage id="order.sale.purchased.continue.button">{
                txt => <button className={classNames(buttonStyle.btn, buttonStyle.orange)}
                               disabled={this.isButtonDisabled()}
                               onClick={_ => progressReceive(order.id)}>{txt}</button>
              }</FormattedMessage>
              <FormattedMessage id="order.sale.purchased.report.button">{
                txt => <button className={classNames(buttonStyle.btn, buttonStyle.redInverse, style.below)}
                               onClick={this.handleOpenReportModal}>{txt}</button>
              }</FormattedMessage>
            </FloatingBottom>
          </section>
        </main>
        <Modal key="modal"
               open={isOpenCancelModal}
               big
               handleClose={() => typeof cancelModalItemNumber === 'number' &&
                 !itemLoadingCancelMap[`${cancelModalItemNumber}`] && this.handleCloseCancelModal()}>
          {typeof cancelModalItemNumber === 'number' && itemLoadingCancelMap[`${cancelModalItemNumber}`] &&
          <Loading type="circle"
                   className={style.loading}/>}
          <section className={classNames(style.modalWrap, {
            [style.disabled]: typeof cancelModalItemNumber === 'number' &&
            itemLoadingCancelMap[`${cancelModalItemNumber}`],
          })}>
            {order.items.length - this.numItemsCancelled() === 1 ? (<Fragment>
                <FormattedMessage id="order.sale.purchased.item.cancel.modal.lastOrder.title">{
                  txt => <h1 className={style.modalTitle}>{txt}</h1>
                }</FormattedMessage>
                <FormattedMessage id="order.sale.purchased.item.cancel.modal.lastOrder.subtitle">{
                  txt => <p className={style.modalSubTitle}>{txt}</p>
                }</FormattedMessage>
              </Fragment>) :
              <FormattedMessage id="order.sale.purchased.item.cancel.modal.title">{
                txt => <h1 className={style.modalTitle}>{txt}</h1>
              }</FormattedMessage>}


            <section className={classNames(formStyle.formWrap)}>
              <section className={classNames(formStyle.row, formStyle.inputRow)}>
                <article className={classNames(formStyle.col)}>
                  <FormattedMessage id="order.sale.purchased.item.cancel.modal.reason">{
                    txt => <label className={formStyle.label}>{txt}</label>
                  }</FormattedMessage>
                  {modalOptions &&
                  <Select options={modalOptions}
                          className={classNames({
                            'light-blue': !!cancelationMotive,
                          })}
                          value={cancelationMotive}
                          disabled={false}
                          searchable={false}
                          handleChange={(option: Option) => this.setState({ cancelationMotive: `${option.value}` })}/>}
                </article>
              </section>
              {cancelationMotive === 'OTHER' && <section className={classNames(formStyle.row, formStyle.inputRow)}>
                <article className={classNames(formStyle.col)}>
                  <FormattedMessage id="order.sale.purchased.item.cancel.modal.otherReason">{
                    txt => <label className={formStyle.label}>{txt}</label>
                  }</FormattedMessage>
                  <textarea name="message"
                            onChange={(e: Event) => this.handleChangeCancelOther(e)}
                            className={classNames(inputStyle.input, style.textareaInput,
                              { [inputStyle.lightBlue]: !!cancelationOtherText })}
                            value={cancelationOtherText}/>
                </article>
              </section>}
              <FormattedMessage id="order.sale.purchased.item.cancel.modal.button.cancel">{
                txt => <button className={classNames(buttonStyle.btn, buttonStyle.red, style.modalButton)}
                               onClick={() => this.handleCancelItem()}>
                  {txt}
                </button>
              }</FormattedMessage>
              <FormattedMessage id="order.sale.purchased.item.cancel.modal.button.close">{
                txt => <button className={classNames(buttonStyle.btn, buttonStyle.darkGrey, style.modalButton)}
                               onClick={() => this.handleCloseCancelModal()}>
                  {txt}
                </button>
              }</FormattedMessage>
            </section>
          </section>
        </Modal>
        <ReportModalContainer color="orange"
                              reportType="TRAVELER_TRACKING"
                              isOpen={reportModalVisible}
                              handleClose={this.handleCloseReportModal}
                              isDiy={order.isDiy}
                              id={order.id}
                              options={options}
                              successIntl="modal.report.traveler.success.text"/>
      </Fragment>
    );
  }
}

Purchased.propTypes = {
  order: PropTypes.object.isRequired,
  buildProducts: PropTypes.func.isRequired,
  buildSummary: PropTypes.func.isRequired,
  // buildPurchaserInfo: PropTypes.func.isRequired,
  setTrackingNumber: PropTypes.func.isRequired,
  confirmItemReceived: PropTypes.func.isRequired,
  progressReceive: PropTypes.func.isRequired,
  itemReceivedLoadingMap: PropTypes.object,
  itemTrackingNumberLoadingMap: PropTypes.object,
  itemTrackingNumberErrorMap: PropTypes.object,
  itemLoadingCancelMap: PropTypes.object,
  itemCancelErrorMap: PropTypes.object,
  cancelItem: PropTypes.func.isRequired,
  isProgressing: PropTypes.bool,
  fetchCancellationMotives: PropTypes.func.isRequired,
  motives: PropTypes.array,
  reportMotives: PropTypes.object,
  locale: PropTypes.object.isRequired,
};



// WEBPACK FOOTER //
// ./src/modules/order/Sale/purchased/components/Purchased.jsx